/**
 * CitationsList Component
 *
 * Displays a list of citations with document information and metadata.
 * Designed to be used within the Citations tab of the ArtefactsPanel.
 */

import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import {
  Input,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownPopover,
  DropdownItem,
} from "@heroui/react";

import { Citation } from "@/types/citations";
import { useArtefactStore } from "@/stores/ArtefactStore";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

import { CitationDocumentViewer } from "../Citations/CitationDocumentViewer";

type GroupingOption = "none" | "message" | "chronological";
type SortOrder = "asc" | "desc";

export const CitationsList: React.FC = () => {
  const { t } = useTranslation();
  const { getCitationsForCurrentChat } = useArtefactStore();
  const allCitations = getCitationsForCurrentChat();

  // Search state
  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Grouping state
  const [groupBy, setGroupBy] = React.useState<GroupingOption>("none");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("desc");
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(new Set());

  // Deduplicate citations by filename and document_id
  const deduplicatedCitations = React.useMemo(() => {
    const seen = new Map<string, Citation>();

    allCitations.forEach((citation) => {
      const key = `${citation.label}-${citation.filename}`;

      if (!seen.has(key)) {
        seen.set(key, citation);
      }
    });

    return Array.from(seen.values());
  }, [allCitations]);

  // Filter citations based on search term
  const filteredCitations = React.useMemo(() => {
    if (!debouncedSearchTerm) {
      return deduplicatedCitations;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();

    return deduplicatedCitations.filter((citation) => {
      const searchableText =
        `${citation.filename} ${citation.snippet || citation.text} ${citation.label}`.toLowerCase();

      return searchableText.includes(searchLower);
    });
  }, [deduplicatedCitations, debouncedSearchTerm]);

  // Group and sort citations
  const groupedCitations = React.useMemo(() => {
    let sortedCitations = [...filteredCitations];

    if (groupBy === "chronological") {
      sortedCitations.sort((a, b) => {
        // Try to get timestamp from citation or use creation order as fallback
        const getTimestamp = (citation: Citation) => {
          // Fallback: use label number as proxy for chronological order
          const labelMatch = citation.label.match(/\[?C?(\d+)\]?/);

          return labelMatch ? parseInt(labelMatch[1], 10) : 0;
        };

        const timeA = getTimestamp(a);
        const timeB = getTimestamp(b);

        return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
      });
    }

    if (groupBy === "none") {
      return [{ key: "all", title: "All Citations", citations: sortedCitations }];
    }

    if (groupBy === "message") {
      const groups = new Map<string, Citation[]>();

      // Group by citation label ranges (every 5 citations)
      sortedCitations.forEach((citation) => {
        const labelMatch = citation.label.match(/\[?C?(\d+)\]?/);
        const citationNum = labelMatch ? parseInt(labelMatch[1], 10) : 0;
        const groupKey = `Group ${Math.floor(citationNum / 5) * 5 + 1}-${Math.floor(citationNum / 5) * 5 + 5}`;

        if (!groups.has(groupKey)) {
          groups.set(groupKey, []);
        }
        groups.get(groupKey)!.push(citation);
      });

      return Array.from(groups.entries()).map(([groupKey, citations]) => {
        return {
          key: groupKey,
          title: `${groupKey} (${citations.length} citations)`,
          subtitle: `Citation range`,
          citations: citations.sort((a, b) => {
            return sortOrder === "asc"
              ? a.label.localeCompare(b.label)
              : b.label.localeCompare(a.label);
          }),
        };
      });
    }

    if (groupBy === "chronological") {
      const groups = new Map<string, Citation[]>();

      sortedCitations.forEach((citation) => {
        // Group by citation order since we don't have timestamps
        const labelMatch = citation.label.match(/\[?C?(\d+)\]?/);
        const citationNum = labelMatch ? parseInt(labelMatch[1], 10) : 0;

        // Group into ranges: 1-10, 11-20, etc.
        const rangeStart = Math.floor((citationNum - 1) / 10) * 10 + 1;
        const rangeEnd = rangeStart + 9;
        const dateKey = `Citations ${rangeStart}-${rangeEnd}`;

        if (!groups.has(dateKey)) {
          groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(citation);
      });

      // Sort groups by citation range
      const sortedGroups = Array.from(groups.entries()).sort(([dateA], [dateB]) => {
        const rangeA = parseInt(dateA.match(/\d+/)?.[0] || "0", 10);
        const rangeB = parseInt(dateB.match(/\d+/)?.[0] || "0", 10);

        return sortOrder === "asc" ? rangeA - rangeB : rangeB - rangeA;
      });

      return sortedGroups.map(([dateKey, citations]) => ({
        key: dateKey,
        title: dateKey,
        subtitle: `${citations.length} citation${citations.length !== 1 ? "s" : ""}`,
        citations: citations.sort((a, b) => {
          return sortOrder === "asc"
            ? a.label.localeCompare(b.label)
            : b.label.localeCompare(a.label);
        }),
      }));
    }

    return [{ key: "all", title: "All Citations", citations: sortedCitations }];
  }, [filteredCitations, groupBy, sortOrder]);

  // Modal state for CitationDocumentViewer
  const [isViewerOpen, setIsViewerOpen] = React.useState(false);
  const [selectedCitation, setSelectedCitation] = React.useState<Citation | null>(null);

  // Show empty state only if there are no citations at all (not just filtered)
  if (deduplicatedCitations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center text-default-500">
          <div className="text-4xl mb-3">📄</div>
          <h3 className="text-lg font-semibold mb-2">{t("No citations found")}</h3>
          <p className="text-sm text-default-400 max-w-sm">
            {t("Document references and citations from AI responses will appear here.")}
          </p>
        </div>
      </div>
    );
  }

  const handleCitationClick = (citation: Citation) => {
    setSelectedCitation(citation);
    setIsViewerOpen(true);
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }

      return newSet;
    });
  };

  const handleGroupingChange = (key: React.Key) => {
    setGroupBy(key as GroupingOption);
    setCollapsedGroups(new Set()); // Reset collapsed state when changing grouping
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  return (
    <div className="h-full flex flex-col">
      {/* Search Input */}
      <div className="p-3 border-b border-divider">
        <div className="flex items-center gap-2">
          <Icon
            className="text-default-400 pointer-events-none flex-shrink-0"
            icon="solar:search-outline"
            width={20}
          />
          <div className="relative flex-1">
            <Input
              className="w-full h-9 pr-8"
              placeholder={t("Search citations...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button
                isIconOnly
                aria-label={t("Clear search")}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 min-w-0"
                data-testid="clear-search"
                size="sm"
                variant="ghost"
                onPress={() => setSearchTerm("")}
              >
                <Icon className="text-default-400" icon="solar:close-circle-linear" width={16} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Grouping Controls */}
      {filteredCitations.length > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-divider bg-default-50">
          <div className="flex items-center gap-2">
            <Dropdown>
              <DropdownTrigger className="inline-flex items-center justify-center gap-2 px-3 h-8 text-sm rounded-medium hover:bg-default-100">
                <Icon icon="solar:menu-dots-outline" width={16} />
                {groupBy === "none" && t("No Grouping")}
                {groupBy === "message" && t("By Message")}
                {groupBy === "chronological" && t("By Time")}
                <Icon icon="solar:alt-arrow-down-linear" width={14} />
              </DropdownTrigger>
              <DropdownPopover>
                <DropdownMenu
                  aria-label={t("Grouping options")}
                  selectedKeys={[groupBy]}
                  selectionMode="single"
                  onAction={handleGroupingChange}
                >
                  <DropdownItem key="none">
                    <div className="flex items-center gap-2">
                      <Icon icon="solar:list-outline" width={16} />
                      {t("No Grouping")}
                    </div>
                  </DropdownItem>
                  <DropdownItem key="message">
                    <div className="flex items-center gap-2">
                      <Icon icon="solar:chat-dots-outline" width={16} />
                      {t("Group by Message")}
                    </div>
                  </DropdownItem>
                  <DropdownItem key="chronological">
                    <div className="flex items-center gap-2">
                      <Icon icon="solar:calendar-outline" width={16} />
                      {t("Group by Time")}
                    </div>
                  </DropdownItem>
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>

            <Button
              isIconOnly
              aria-label={sortOrder === "asc" ? t("Sort descending") : t("Sort ascending")}
              className="h-8"
              size="sm"
              variant="ghost"
              onPress={toggleSortOrder}
            >
              <Icon
                className={sortOrder === "desc" ? "transform rotate-180" : ""}
                icon={
                  sortOrder === "asc"
                    ? "solar:sort-by-alphabet-outline"
                    : "solar:sort-by-alphabet-outline"
                }
                width={16}
              />
            </Button>
          </div>

          <span className="text-xs text-default-500">
            {filteredCitations.length} citation{filteredCitations.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Citations List */}
      <div className="flex-1 overflow-y-auto">
        {filteredCitations.length === 0 ? (
          // No search results
          <div className="flex items-center justify-center h-full p-6">
            <div className="text-center text-default-500">
              <Icon
                className="text-4xl mb-3 mx-auto"
                icon="solar:search-minimalistic-linear"
                width={48}
              />
              <h3 className="text-lg font-semibold mb-2">{t("No results found")}</h3>
              <p className="text-sm text-default-400 max-w-sm">
                {t("Try adjusting your search terms or clear the search to see all citations.")}
              </p>
            </div>
          </div>
        ) : (
          <div className="p-3 space-y-4">
            {groupedCitations.map((group) => (
              <div key={group.key} className="space-y-3">
                {/* Group Header - only show if not "all" group or if there are multiple groups */}
                {(group.key !== "all" || groupedCitations.length > 1) && (
                  <div className="flex items-center justify-between">
                    <button
                      className="flex items-center gap-2 text-sm font-medium text-default-700 hover:text-default-900 transition-colors"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <Icon
                        icon={
                          collapsedGroups.has(group.key)
                            ? "solar:alt-arrow-right-linear"
                            : "solar:alt-arrow-down-linear"
                        }
                        width={16}
                      />
                      <span>{group.title}</span>
                      <span className="text-xs text-default-400">({group.citations.length})</span>
                    </button>
                    {(group as any).subtitle && (
                      <span className="text-xs text-default-400">{(group as any).subtitle}</span>
                    )}
                  </div>
                )}

                {/* Group Citations - collapsed state */}
                {!collapsedGroups.has(group.key) && (
                  <div className={group.key !== "all" ? "ml-4 space-y-3" : "space-y-3"}>
                    {group.citations.map((citation, index) => (
                      <div
                        key={`${citation.label}-${index}`}
                        className="p-3 bg-default-50 rounded-lg hover:bg-default-100 transition-colors cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleCitationClick(citation)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCitationClick(citation);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                            {citation.label.startsWith("[")
                              ? citation.label
                              : `[${citation.label}]`}
                          </span>
                          <Icon
                            className="text-default-400 ml-2 flex-shrink-0"
                            icon="solar:document-text-outline"
                            width={16}
                          />
                        </div>
                        <h4 className="text-sm font-medium text-default-700 mb-1 line-clamp-1">
                          {citation.filename}
                        </h4>
                        <p className="text-xs text-default-500 line-clamp-2 leading-relaxed">
                          {citation.snippet || citation.text}
                        </p>
                        {citation.score && (
                          <div className="mt-2 text-xs text-default-400">
                            Relevance: {Math.round(citation.score * 100)}%
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Citation Document Viewer Modal */}
      <CitationDocumentViewer
        citation={selectedCitation}
        isOpen={isViewerOpen}
        onOpenChange={setIsViewerOpen}
      />
    </div>
  );
};
